// The voices a participant may choose for Aria, and the one fixed voice the group AI Debrief speaks with.

/** Stored on the user, so the values are part of the data contract, not display strings. */
export enum AiCoachVoiceId {
  FEMALE = 'female',
  MALE = 'male'
}

export type AiCoachVoicePreset = {
  id: AiCoachVoiceId
  /** Translation key — never render this list's text directly. */
  labelKey: string
  descriptionKey: string
  /** Gemini Live prebuilt voice name (realtime 1:1 coach). */
  liveVoiceName: string
}

export const DEFAULT_AI_COACH_VOICE_PRESET_ID = AiCoachVoiceId.FEMALE

export const AI_COACH_VOICE_PRESETS: AiCoachVoicePreset[] = [
  {
    id: AiCoachVoiceId.FEMALE,
    labelKey: 'settings.coach.voice.female',
    descriptionKey: 'settings.coach.voice.femaleDescription',
    // Sulafat is the warm, low-energy voice of the set; the breezy ones read as an American sales greeting.
    liveVoiceName: 'Sulafat'
  },
  {
    id: AiCoachVoiceId.MALE,
    labelKey: 'settings.coach.voice.male',
    descriptionKey: 'settings.coach.voice.maleDescription',
    liveVoiceName: 'Achird'
  }
]

// Unknown or absent ids fall back to the default rather than throwing — this runs on every mint.
export function getAiCoachVoicePreset(id?: string): AiCoachVoicePreset {
  return AI_COACH_VOICE_PRESETS.find((preset) => preset.id === id) || AI_COACH_VOICE_PRESETS[0]
}

/**
 * How the Google TTS voice is spoken. Chirp3 exposes no pitch control, so the lower register comes from
 * resampling the rendered audio rather than from a synthesis parameter.
 */
export type AiCoachTtsVoice = {
  /** Chirp3-HD voice id, e.g. 'Sulafat'. Combined with a locale to form the full voice name. */
  chirp3Voice: string
  /** Google TTS `speakingRate`. Above 1 to buy back the tempo the resample below takes away. */
  speakingRate: number
  /** Playback slowdown applied by resampling: below 1 lowers the register and lengthens the audio. */
  resampleFactor: number
  /** Insert a short pause between sentences. */
  withSentencePauses: boolean
}

/**
 * The one voice the group AI Debrief speaks with, for every team and every language.
 *
 * Deliberately NOT part of `AI_COACH_VOICE_PRESETS`: a participant chooses how Aria sounds to them alone,
 * while the debrief plays a single audio track to a whole team and nobody may change it. The numbers are
 * the approved review sample — `pure-js/assets/ai-coach-voices/manifest.json` records how it was made.
 */
export const AI_COACH_DEBRIEF_VOICE: AiCoachTtsVoice = {
  chirp3Voice: 'Sulafat',
  // Untuned: resampling rewrites the samples but not the declared rate, which a live room hears as
  // silence. Prove any lower register against a real debrief, one knob at a time, before restoring it.
  speakingRate: 1,
  resampleFactor: 1,
  withSentencePauses: false
}

/** Chirp3-HD voices are named `{locale}-Chirp3-HD-{voice}`. One definition, used by every TTS caller. */
export function buildChirp3VoiceName(languageCode: string, chirp3Voice = AI_COACH_DEBRIEF_VOICE.chirp3Voice): string {
  return `${languageCode}-Chirp3-HD-${chirp3Voice}`
}

/** Identifies how audio was spoken, so cached audio can be told apart from audio in a re-tuned voice. */
export function buildVoiceTuningKey(voice: AiCoachTtsVoice): string {
  return `${voice.chirp3Voice}:${voice.speakingRate}:${voice.resampleFactor}:${voice.withSentencePauses}`
}
